import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import apiClient from '@/lib/api-client'
import { useProfileLocation } from '@/contexts/ProfileLocationContext'
import type { ServicePrice } from '@/types'
import { formatCurrencyAmount } from '@/utils/currency'
import { Button } from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Loader2, Plus, Edit2, Trash2, X, Package, Users } from 'lucide-react'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'
import { teamApi } from '@/services/teamApi'
import type { StaffMember } from '@/types'

const serviceSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  price: z.number().min(0).nullable(),
  price_display: z.enum(['amount', 'from', 'contact', 'hidden']),
  duration_minutes: z.number().min(15),
  category_id: z.string().nullable(),
  is_active: z.boolean(),
  is_multi_booking: z.boolean(),
  max_capacity: z.number().int().positive().nullable(),
})

type ServiceFormData = z.infer<typeof serviceSchema>

interface Service {
  id: string
  name: string
  description: string | null
  price: number | null
  price_display: 'amount' | 'from' | 'contact' | 'hidden'
  duration_minutes: number | null
  is_active: boolean
  category_id: string | null
  is_multi_booking: boolean
  max_capacity: number | null
  prices?: ServicePrice[]
}

interface ServiceCategory {
  id: string
  name: string
  services: Service[]
}

// Material types for service association
interface MaterialVariant {
  id: string
  name: string
  sku: string | null
  price_adjustment: number
  price_adjustment_formatted: string
  effective_price: number
  effective_price_formatted: string
  attributes: Record<string, unknown>
  is_active: boolean
  sort_order: number
}

interface ServiceMaterial {
  id: string
  name: string
  description: string | null
  unit: string
  base_price: number
  base_price_formatted: string
  allows_selection: boolean
  is_required: boolean
  category: string | null
  is_associated: boolean
  pivot: {
    is_required: boolean
    allows_variant_selection: boolean
    show_in_booking: boolean
    default_quantity: number
    included_in_price: boolean
  } | null
  variants: MaterialVariant[]
}

export default function ServiceManager() {
  const { t } = useTranslation('dashboard')
  const embedded = useDashboardEmbedded()
  const queryClient = useQueryClient()
  const { currentProfile } = useProfileLocation()
  const supportedCurrencies = currentProfile?.supported_currencies?.length
    ? currentProfile.supported_currencies
    : [currentProfile?.currency || 'EUR']
  const [showForm, setShowForm] = useState(false)
  const [editingService, setEditingService] = useState<Service | null>(null)

  // Fetch services
  const { data: serviceData, isLoading } = useQuery<{ categories: ServiceCategory[]; uncategorized: Service[] }>({
    queryKey: ['services'],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { categories: ServiceCategory[]; uncategorized: Service[] }
      }>('/user/services')
      return response.data.data
    },
  })

  // Create service mutation
  const createMutation = useMutation({
    mutationFn: async (data: Partial<Service>) => {
      const response = await apiClient.post('/user/services', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
      setShowForm(false)
    },
  })

  // Update service mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Service> & { id: string }) => {
      const response = await apiClient.put(`/user/services/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
      setEditingService(null)
    },
  })

  // Delete service mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/user/services/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
    },
  })

  // Update service materials mutation
  const updateMaterialsMutation = useMutation({
    mutationFn: async ({ serviceId, materials }: { serviceId: string; materials: Array<{ material_id: string; is_required: boolean; allows_variant_selection: boolean; show_in_booking: boolean; default_quantity: number; included_in_price: boolean }> }) => {
      const response = await apiClient.post(`/user/materials/service/${serviceId}`, { materials })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const categories = serviceData?.categories || []
  const uncategorized = serviceData?.uncategorized || []

  return (
    <div className="p-6 lg:p-8">
      <div className={`flex items-center justify-between ${embedded ? 'mb-6' : 'mb-8'}`}>
        {!embedded && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('services.title')}</h1>
            <p className="text-gray-500">{t('services.subtitle')}</p>
          </div>
        )}
        {embedded && <div />}
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('services.addService')}
        </Button>
      </div>

      {/* Categories */}
      {categories.map((category) => (
        <div key={category.id} className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{category.name}</h2>
          <div className="space-y-3">
            {category.services.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onEdit={() => setEditingService(service)}
                onDelete={() => deleteMutation.mutate(service.id)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Uncategorized Services */}
      {uncategorized.length > 0 && (
        <div className="mb-8">
          {categories.length > 0 && (
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('services.otherServices')}</h2>
          )}
          <div className="space-y-3">
            {uncategorized.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onEdit={() => setEditingService(service)}
                onDelete={() => deleteMutation.mutate(service.id)}
              />
            ))}
          </div>
        </div>
      )}

      {categories.length === 0 && uncategorized.length === 0 && !showForm && (
        <div className="text-center py-12">
          <p className="text-gray-400 mb-4">{t('services.noServices')}</p>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('services.addFirstService')}
          </Button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showForm || editingService) && (
        <ServiceFormModal
          service={editingService}
          categories={categories}
          supportedCurrencies={supportedCurrencies}
          primaryCurrency={currentProfile?.currency || 'EUR'}
          onSubmit={async (data) => {
            // Extract selected materials from data
            const { _selectedMaterials, _staffCapabilities, ...serviceData } = data as Partial<Service> & {
              _selectedMaterials?: SelectedMaterial[]
              _staffCapabilities?: { user_ids: string[] }
            }

            let serviceId: string

            if (editingService) {
              // Update existing service
              await updateMutation.mutateAsync({ id: editingService.id, ...serviceData })
              serviceId = editingService.id
            } else {
              // Create new service
              const result = await createMutation.mutateAsync(serviceData)
              // Extract the new service ID from the response
              serviceId = result.data?.id || result.data?.data?.id
            }

            // Update materials if we have a service ID and selected materials
            if (serviceId && _selectedMaterials && _selectedMaterials.length > 0) {
              await updateMaterialsMutation.mutateAsync({
                serviceId,
                materials: _selectedMaterials,
              })
            }

            if (serviceId && _staffCapabilities) {
              await apiClient.put(`/user/services/${serviceId}/staff-capabilities`, _staffCapabilities)
            }
          }}
          onClose={() => {
            setShowForm(false)
            setEditingService(null)
          }}
          isLoading={createMutation.isPending || updateMutation.isPending || updateMaterialsMutation.isPending}
        />
      )}
    </div>
  )
}

interface ServiceCardProps {
  service: Service
  onEdit: () => void
  onDelete: () => void
}

function ServiceCard({ service, onEdit, onDelete }: ServiceCardProps) {
  const { t } = useTranslation('dashboard')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const priceLabel = service.prices?.length
    ? service.prices
        .map((p) => formatCurrencyAmount(Number(p.price), p.currency))
        .join(' · ')
    : service.price && service.price_display !== 'hidden'
      ? `$${service.price.toFixed(2)}`
      : null

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-gray-900">{service.name}</h3>
              {!service.is_active && (
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
                  {t('services.inactive')}
                </span>
              )}
            </div>
            {service.description && (
              <p className="text-sm text-gray-600 mt-1">{service.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm">
              {priceLabel && service.price_display !== 'contact' && service.price_display !== 'hidden' && (
                <span className="font-medium text-primary">
                  {service.price_display === 'from' && `${t('services.form.fromPrice').replace('$X', '')} `}
                  {priceLabel}
                </span>
              )}
              {service.price_display === 'contact' && (
                <span className="text-gray-500">{t('services.contactForPricing')}</span>
              )}
              {service.price_display === 'hidden' && (
                <span className="text-gray-400">{t('services.noPriceShown')}</span>
              )}
              {service.duration_minutes && (
                <span className="text-gray-500">{t('services.duration', { minutes: service.duration_minutes })}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onEdit}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            {showDeleteConfirm ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onDelete}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                >
                  {t('services.delete.delete')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                >
                  {t('services.delete.cancel')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface ServiceFormModalProps {
  service: Service | null
  categories: ServiceCategory[]
  supportedCurrencies: string[]
  primaryCurrency: string
  onSubmit: (data: Partial<Service> & {
    _selectedMaterials?: SelectedMaterial[]
    _staffCapabilities?: { user_ids: string[] }
    prices?: ServicePrice[]
  }) => Promise<void>
  onClose: () => void
  isLoading: boolean
}

// Material selection state for a single material
interface SelectedMaterial {
  material_id: string
  is_required: boolean
  allows_variant_selection: boolean
  show_in_booking: boolean
  default_quantity: number
  included_in_price: boolean
}

function ServiceFormModal({
  service,
  categories,
  supportedCurrencies,
  primaryCurrency,
  onSubmit,
  onClose,
  isLoading,
}: ServiceFormModalProps) {
  const { t } = useTranslation('dashboard')

  const [currencyPrices, setCurrencyPrices] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    supportedCurrencies.forEach((currency) => {
      const existing = service?.prices?.find((p) => p.currency === currency);
      initial[currency] = existing ? String(existing.price) : '';
    });
    return initial;
  });

  // State for material selection
  const [selectedMaterials, setSelectedMaterials] = useState<SelectedMaterial[]>([])
  const [showMaterials, setShowMaterials] = useState(false)

  // Staff capability state
  const [restrictsStaff, setRestrictsStaff] = useState(false)
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([])
  const [showStaffCapabilities, setShowStaffCapabilities] = useState(false)

  const { data: teamData } = useQuery({
    queryKey: ['teams', 'mine'],
    queryFn: () => teamApi.getMyTeams(),
    enabled: !!service?.id,
  })

  const teamId = teamData?.[0]?.id

  const { data: assignableStaff } = useQuery<StaffMember[]>({
    queryKey: ['team-staff', teamId],
    queryFn: () => teamApi.getStaff(teamId!),
    enabled: !!teamId && !!service?.id,
  })

  const { data: staffCapabilities } = useQuery<{
    restricts_staff: boolean
    user_ids: string[]
  }>({
    queryKey: ['service-staff-capabilities', service?.id],
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean
        data: { restricts_staff: boolean; user_ids: string[] }
      }>(`/user/services/${service?.id}/staff-capabilities`)
      return response.data.data
    },
    enabled: !!service?.id,
  })

  useEffect(() => {
    if (staffCapabilities) {
      setRestrictsStaff(staffCapabilities.restricts_staff)
      setSelectedStaffIds(staffCapabilities.user_ids)
    }
  }, [staffCapabilities])

  // Fetch available materials when editing an existing service
  const { data: availableMaterials } = useQuery<ServiceMaterial[]>({
    queryKey: ['materials', 'available', service?.id],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: ServiceMaterial[] }>(
        `/user/materials/service/${service?.id}/available`
      )
      return response.data.data
    },
    enabled: !!service?.id,
  })

  // Initialize selected materials when availableMaterials changes
  useEffect(() => {
    if (availableMaterials) {
      const initial: SelectedMaterial[] = availableMaterials
        .filter((m) => m.is_associated && m.pivot)
        .map((m) => ({
          material_id: m.id,
          is_required: m.pivot!.is_required,
          allows_variant_selection: m.pivot!.allows_variant_selection,
          show_in_booking: m.pivot!.show_in_booking,
          default_quantity: m.pivot!.default_quantity,
          included_in_price: m.pivot!.included_in_price,
        }))
      setSelectedMaterials(initial)
    }
  }, [availableMaterials])

  // Group materials by category
  const materialsByCategory = availableMaterials?.reduce((acc, material) => {
    const category = material.category || 'Uncategorized'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(material)
    return acc
  }, {} as Record<string, ServiceMaterial[]>)

  const form = useForm<ServiceFormData>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      name: service?.name || '',
      description: service?.description || '',
      price: service?.price || null,
      price_display: service?.price_display || 'amount',
      duration_minutes: service?.duration_minutes || 60,
      category_id: service?.category_id || null,
      is_active: service?.is_active ?? true,
      is_multi_booking: service?.is_multi_booking ?? false,
      max_capacity: service?.max_capacity ?? null,
    },
  })

  const handleSubmit = form.handleSubmit(async (data) => {
    const prices =
      supportedCurrencies.length > 1
        ? supportedCurrencies
            .map((currency) => ({
              currency,
              price: parseFloat(currencyPrices[currency] || '0'),
              is_primary: currency === primaryCurrency,
            }))
            .filter((entry) => !Number.isNaN(entry.price) && entry.price >= 0)
        : undefined;

    const primaryPrice = prices?.find((p) => p.is_primary)?.price ?? data.price;

    await onSubmit({
      name: data.name,
      description: data.description || null,
      price: primaryPrice ?? data.price,
      price_display: data.price_display,
      duration_minutes: data.duration_minutes,
      category_id: data.category_id,
      is_active: data.is_active,
      is_multi_booking: data.is_multi_booking,
      max_capacity: data.max_capacity,
      prices,
      _selectedMaterials: selectedMaterials,
      _staffCapabilities: service
        ? {
            user_ids: restrictsStaff ? selectedStaffIds : [],
          }
        : undefined,
    })
  })

  const toggleStaffMember = (userId: string) => {
    setSelectedStaffIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    )
  }

  const enableStaffRestriction = () => {
    setRestrictsStaff(true)
    if (selectedStaffIds.length === 0 && assignableStaff?.length) {
      setSelectedStaffIds(assignableStaff.map((s) => s.id))
    }
  }

  // Toggle material selection
  const toggleMaterial = (materialId: string) => {
    setSelectedMaterials((prev) => {
      const exists = prev.find((m) => m.material_id === materialId)
      if (exists) {
        return prev.filter((m) => m.material_id !== materialId)
      }
      return [
        ...prev,
        {
          material_id: materialId,
          is_required: false,
          allows_variant_selection: true,
          show_in_booking: true,
          default_quantity: 1,
          included_in_price: true,
        },
      ]
    })
  }

  // Update material settings
  const updateMaterialSetting = (materialId: string, key: keyof SelectedMaterial, value: boolean | number) => {
    setSelectedMaterials((prev) =>
      prev.map((m) => (m.material_id === materialId ? { ...m, [key]: value } : m))
    )
  }

  // Check if a material is selected
  const isMaterialSelected = (materialId: string) => {
    return selectedMaterials.some((m) => m.material_id === materialId)
  }

  // Get material selection data
  const getMaterialSelection = (materialId: string) => {
    return selectedMaterials.find((m) => m.material_id === materialId)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">
            {service ? t('services.form.editService') : t('services.form.addService')}
          </h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.form.name')} *</label>
            <Input
              {...form.register('name')}
              placeholder={t('services.form.namePlaceholder')}
            />
            {form.formState.errors.name && (
              <p className="text-sm text-red-500 mt-1">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.form.description')}</label>
            <textarea
              {...form.register('description')}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder={t('services.form.descriptionPlaceholder')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {supportedCurrencies.length > 1 ? (
              <div className="col-span-2 space-y-3">
                <label className="block text-sm font-medium text-gray-700">Prices by currency</label>
                {supportedCurrencies.map((currency) => (
                  <div key={currency} className="flex items-center gap-3">
                    <span className="w-12 text-sm font-medium text-gray-600">{currency}</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={currencyPrices[currency] || ''}
                      onChange={(e) =>
                        setCurrencyPrices((prev) => ({ ...prev, [currency]: e.target.value }))
                      }
                      placeholder="0.00"
                    />
                    {currency === primaryCurrency && (
                      <span className="text-xs text-muted-foreground">Primary</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.form.price')}</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  {...form.register('price', { valueAsNumber: true })}
                  placeholder={t('services.form.pricePlaceholder')}
                />
              </div>
            )}
            <div className={supportedCurrencies.length > 1 ? 'col-span-2' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.form.display')}</label>
              <select
                {...form.register('price_display')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="amount">{t('services.form.showPrice')}</option>
                <option value="from">{t('services.form.fromPrice')}</option>
                <option value="contact">{t('services.form.contactPrice')}</option>
                <option value="hidden">{t('services.form.hidePrice')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.form.duration')}</label>
              <Input
                type="number"
                min="15"
                {...form.register('duration_minutes', { valueAsNumber: true })}
                placeholder={t('services.form.durationPlaceholder')}
              />
              {form.formState.errors.duration_minutes && (
                <p className="text-sm text-red-500 mt-1">{form.formState.errors.duration_minutes.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('services.form.category')}</label>
              <select
                {...form.register('category_id')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">{t('services.form.noCategory')}</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              {...form.register('is_active')}
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              {t('services.form.isActive')}
            </label>
          </div>

          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-medium text-gray-700">Multi-booking</p>
                <p className="text-sm text-gray-500">
                  Allow multiple customers to book the same time slot (events, webinars)
                </p>
              </div>
              <button
                type="button"
                onClick={() => form.setValue('is_multi_booking', !form.getValues('is_multi_booking'))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  form.getValues('is_multi_booking') ? 'bg-primary' : 'bg-gray-200'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.getValues('is_multi_booking') ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {form.getValues('is_multi_booking') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Capacity
                </label>
                <Input
                  type="number"
                  min="1"
                  {...form.register('max_capacity', { valueAsNumber: true })}
                  placeholder="Unlimited"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave empty for unlimited
                </p>
              </div>
            )}
          </div>

          {/* Staff capabilities — only when editing and team has assignable staff */}
          {service && assignableStaff && assignableStaff.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <button
                type="button"
                onClick={() => setShowStaffCapabilities(!showStaffCapabilities)}
                className="flex items-center justify-between w-full mb-4"
              >
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-700">Staff who can perform this service</span>
                  <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                    {restrictsStaff ? `${selectedStaffIds.length} selected` : 'All staff'}
                  </span>
                </div>
                <span className={`transform transition-transform ${showStaffCapabilities ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>

              {showStaffCapabilities && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">
                    By default every team member can perform every service. Restrict only when specific
                    people should offer this service.
                  </p>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={restrictsStaff}
                      onChange={(e) => {
                        if (e.target.checked) {
                          enableStaffRestriction()
                        } else {
                          setRestrictsStaff(false)
                          setSelectedStaffIds([])
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">Limit to selected staff</span>
                  </label>

                  {restrictsStaff && (
                    <div className="space-y-2">
                      {assignableStaff.map((member) => (
                        <label
                          key={member.id}
                          className="flex items-center gap-3 p-3 border rounded-lg border-gray-200"
                        >
                          <input
                            type="checkbox"
                            checked={selectedStaffIds.includes(member.id)}
                            onChange={() => toggleStaffMember(member.id)}
                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <span className="text-sm font-medium text-gray-900">{member.full_name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Materials Section - only show when editing existing service */}
          {service && availableMaterials && (
            <div className="border-t pt-4 mt-4">
              <button
                type="button"
                onClick={() => setShowMaterials(!showMaterials)}
                className="flex items-center justify-between w-full mb-4"
              >
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-700">Service Materials</span>
                  <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                    {selectedMaterials.length} selected
                  </span>
                </div>
                <span className={`transform transition-transform ${showMaterials ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>

              {showMaterials && (
                <div className="space-y-4">
                  {Object.entries(materialsByCategory || {}).map(([category, materials]) => (
                    <div key={category}>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">{category}</h4>
                      <div className="space-y-2">
                        {materials.map((material) => {
                          const isSelected = isMaterialSelected(material.id)
                          const selection = getMaterialSelection(material.id)
                          return (
                            <div
                              key={material.id}
                              className={`p-3 border rounded-lg ${
                                isSelected ? 'border-primary bg-primary/5' : 'border-gray-200'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleMaterial(material.id)}
                                  className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-gray-900">{material.name}</span>
                                    <span className="text-sm text-gray-500">
                                      {material.base_price_formatted}/{material.unit}
                                    </span>
                                  </div>
                                  {material.description && (
                                    <p className="text-sm text-gray-500 mt-1">{material.description}</p>
                                  )}
                                  {material.variants?.length > 0 && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      {material.variants?.length} variant(s) available
                                    </p>
                                  )}

                                  {isSelected && (
                                    <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-3">
                                      <label className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={selection?.is_required ?? false}
                                          onChange={(e) =>
                                            updateMaterialSetting(material.id, 'is_required', e.target.checked)
                                          }
                                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm text-gray-700">Required</span>
                                      </label>
                                      <label className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={selection?.allows_variant_selection ?? true}
                                          onChange={(e) =>
                                            updateMaterialSetting(material.id, 'allows_variant_selection', e.target.checked)
                                          }
                                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm text-gray-700">Allow variant selection</span>
                                      </label>
                                      <label className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={selection?.show_in_booking ?? true}
                                          onChange={(e) =>
                                            updateMaterialSetting(material.id, 'show_in_booking', e.target.checked)
                                          }
                                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm text-gray-700">Show in booking</span>
                                      </label>
                                      <label className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={selection?.included_in_price ?? true}
                                          onChange={(e) =>
                                            updateMaterialSetting(material.id, 'included_in_price', e.target.checked)
                                          }
                                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm text-gray-700">Included in price</span>
                                      </label>
                                      <div className="col-span-2">
                                        <label className="block text-sm text-gray-700 mb-1">Default quantity</label>
                                        <Input
                                          type="number"
                                          min="0"
                                          value={selection?.default_quantity ?? 1}
                                          onChange={(e) =>
                                            updateMaterialSetting(
                                              material.id,
                                              'default_quantity',
                                              parseInt(e.target.value) || 0
                                            )
                                          }
                                          className="w-24"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Show message when creating new service */}
          {!service && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 text-gray-500">
                <Package className="w-5 h-5" />
                <span className="text-sm">
                  Materials can be assigned after creating the service.
                </span>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('services.form.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t('services.form.saving') : service ? t('services.form.update') : t('services.form.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
